// W3C Trace Context (https://www.w3.org/TR/trace-context/).
//
// We mint a `traceparent` on every outbound request so a browser action can be
// followed all the way through tio-api and the downstream services. tio-api reads
// this header (see SetRequestContext), continues the trace, and stamps the
// trace_id into its logs — the join key across RUM, logs, and errors in OpenSearch.
//
// tio-data deliberately doesn't depend on tio-common, so this lives here next to
// the request pipeline that uses it.

const randomHex = (bytes: number) => {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  let hex = '';
  for (const byte of buffer) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
};

// W3C forbids an all-zero trace-id or span-id. The odds are astronomically small
// (2^-128 / 2^-64), but guard so we never emit an invalid traceparent.
const nonZeroHex = (bytes: number) => {
  let hex = randomHex(bytes);
  while (/^0+$/.test(hex)) {
    hex = randomHex(bytes);
  }
  return hex;
};

// Page-scoped trace. Every outbound request *and* every RUM telemetry event in a
// page load shares one trace-id (each request gets a fresh child span-id), so a
// telemetry event can be followed to the backend trace it belongs to — the join
// key across RUM, logs, and traces. The TelemetryService mints a new trace per
// navigation (startNewTrace); the first use of a page lazily mints one.
let pageTraceId: string | null = null;

/** The active page's trace-id, lazily minted on first use (initial page load). */
export const currentTraceId = (): string => {
  if (!pageTraceId) {
    pageTraceId = nonZeroHex(16);
  }
  return pageTraceId;
};

/** Begin a new page trace. Called on SPA navigation so each page is its own trace. */
export const startNewTrace = (): void => {
  pageTraceId = nonZeroHex(16);
};

/**
 * A sampled `traceparent` for an outbound request:
 *   `00-<32 hex page trace-id>-<16 hex fresh span-id>-01`
 *
 * The trace-id is the current page trace (so all of a page's requests group
 * under one trace); the span-id is fresh per request.
 */
export const generateTraceparent = () => `00-${currentTraceId()}-${nonZeroHex(8)}-01`;
